import { Map } from "immutable";
import { omitBy } from "lodash";

import itemCardFunctions from "../../../features/itemCard/itemCardFunctions";
import { ViewCardSizesRecord } from "../../../types/immutableRecords/model/display/ViewCardSizesRecord";
import { keyArrayFromMap } from "../../../utils/immutableExtensions";
import { appStateSelectors } from "../../appState";
import { characterWidthMapSelectors } from "../../characterWidthMap";
import { definitionSelectors } from "../../definition";

// This will work out if all size values are the same
// If anything changes, we will need to recalculate our
// item card size.
// If necessary, we can probably break it down separately
// for each view type if absolutely necessary
// Note: imagePath only matters if it exists or not, so converting
// to boolean for test

const areEquivalent = (a, b) => {
  return a.width === b.width && a.height === b.height;
};

// TODO: Add in the mobile values
// itemCardNestedTitleMetrics: TextMetricsRecord(),
// itemCardNarrativeNestedTitleMetrics: TextMetricsRecord(),
// itemCardNestedSummaryMetrics: TextMetricsRecord(),
// itemCardNarrativeNestedSummaryMetrics: TextMetricsRecord(),

// const areMobilePortraitSizeValuesSame = (newItem, prevItem) =>
//   newItem &&
//   prevItem &&
//   newItem.itemCardNestedTitleMetrics === prevItem.itemCardNestedTitleMetrics &&
//   newItem.itemCardNarrativeNestedTitleMetrics === prevItem.itemCardNarrativeNestedTitleMetrics &&
//   newItem.itemCardNestedSummaryMetrics === prevItem.itemCardNestedSummaryMetrics &&
//   newItem.itemCardNarrativeNestedSummaryMetrics === prevItem.itemCardNarrativeNestedSummaryMetrics &&
//   newItem.dateTextSize === prevItem.dateTextSize &&
//   newItem.durationTextSize === prevItem.durationTextSize &&
//   newItem.narrativeTextSize === prevItem.narrativeTextSize &&
//   newItem.identifierSize === prevItem.identifierSize;

const areNormalSizeValuesSame = (newItem, prevItem) =>
  newItem &&
  prevItem &&
  newItem.titleMetrics === prevItem.titleMetrics &&
  newItem.nestedTitleMetrics === prevItem.nestedTitleMetrics &&
  newItem.narrativeNestedTitleMetrics === prevItem.narrativeNestedTitleMetrics &&
  newItem.summaryMetrics === prevItem.summaryMetrics &&
  newItem.dateTextSize === prevItem.dateTextSize &&
  newItem.durationTextSize === prevItem.durationTextSize &&
  newItem.narrativeTextSize === prevItem.narrativeTextSize &&
  newItem.identifierSize === prevItem.identifierSize &&
  newItem.relatedItemsByType === prevItem.relatedItemsByType &&
  newItem.blockingIdentifiers === prevItem.blockingIdentifiers &&
  newItem.blockedByIdentifiers === prevItem.blockedByIdentifiers &&
  Boolean(newItem.imagePath) === Boolean(prevItem.imagePath);

// Merge in the ItemRecord for any items that changed
const insertNewItems = (state, newIds) => {
  return state.withMutations(s => {
    newIds.forEach(cId => {
      s.set(cId, ViewCardSizesRecord());
    });
  });
};

const reducer = (state = Map(), action, newGlobalState, prevGlobalState) => {
  // If we have no data loaded yet, don't bother deriving anything
  if (!newGlobalState.isDataLoaded) {
    return state;
  }

  const newDisplayItems = newGlobalState.display.displayItems;
  const prevDisplayItems = prevGlobalState.display.displayItems;

  const newFontSize = appStateSelectors.getFontSize(newGlobalState);
  const prevFontSize = appStateSelectors.getFontSize(prevGlobalState);
  const newWidthMap = characterWidthMapSelectors.getCharacterWidthMap(newGlobalState);
  const prevWidthMap = characterWidthMapSelectors.getCharacterWidthMap(prevGlobalState);

  const newTimelineSettings = definitionSelectors.getTimelineViewSettings(newGlobalState);
  const prevTimelineSettings = definitionSelectors.getTimelineViewSettings(prevGlobalState);

  const newNarrativeSettings = definitionSelectors.getNarrativeViewSettings(newGlobalState);
  const prevNarrativeSettings = definitionSelectors.getNarrativeViewSettings(prevGlobalState);

  const newMindmapSettings = definitionSelectors.getMindmapViewSettings(newGlobalState);
  const prevMindmapSettings = definitionSelectors.getMindmapViewSettings(prevGlobalState);

  const newRelationshipSettings = definitionSelectors.getRelationshipViewSettings(newGlobalState);
  const prevRelationshipSettings = definitionSelectors.getRelationshipViewSettings(prevGlobalState);

  const forceCalculation = newFontSize !== prevFontSize || newWidthMap !== prevWidthMap;

  const forceTimeline = forceCalculation || newTimelineSettings !== prevTimelineSettings;
  const forceNarrative = forceCalculation || newNarrativeSettings !== prevNarrativeSettings;
  const forceMindmap = forceCalculation || newMindmapSettings !== prevMindmapSettings;
  const forceRelationship = forceCalculation || newRelationshipSettings !== prevRelationshipSettings;

  // If nothing changed, then we don't need to do anything further
  if (
    newDisplayItems === prevDisplayItems &&
    !forceCalculation &&
    !forceTimeline &&
    !forceNarrative &&
    !forceMindmap &&
    !forceRelationship
  ) {
    return state;
  }

  let newState = state;

  // Otherwise, for those that have changed, test to see if any of the
  // relevant text size values have changed, and if they have, calculate
  // new sizes
  const newIds = keyArrayFromMap(newDisplayItems.filter((newItem, id) => !prevDisplayItems.has(id)));
  const deletedIds = keyArrayFromMap(prevDisplayItems.filter((prevItem, id) => !newDisplayItems.has(id)));

  // Remove any that should not exist anymore!
  if (deletedIds.length) {
    newState = newState.deleteAll(deletedIds);
  }

  // Create items for anything new, so that they already exist when we go to update them in the next section
  if (newIds.length) {
    newState = insertNewItems(newState, newIds);
  }

  const useMindmapSettings = newMindmapSettings.set("showIdentifier", false);

  // Now we need
  return newState.withMutations(s => {
    newState.forEach((currSizes, id) => {
      const newItem = newDisplayItems.get(id);
      const prevItem = prevDisplayItems.get(id);

      const isChanged = newItem !== prevItem && !areNormalSizeValuesSame(newItem, prevItem);

      let changes = {};

      if (forceTimeline || isChanged) {
        changes.timelineCardSize = itemCardFunctions.calcItemCardSize(
          newItem,
          newTimelineSettings,
          newFontSize,
          newWidthMap
        );
      }
      if (forceNarrative || isChanged) {
        changes.narrativeCardSize = itemCardFunctions.calcItemCardSize(
          newItem,
          newNarrativeSettings,
          newFontSize,
          newWidthMap
        );
      }
      if (forceRelationship || isChanged) {
        changes.relationshipCardSize = itemCardFunctions.calcItemCardSize(
          newItem,
          newRelationshipSettings,
          newFontSize,
          newWidthMap,
          newItem.super ? "nestedTitleMetrics" : null
        );
      }
      if (forceMindmap || isChanged) {
        changes.mindmapCardSize = itemCardFunctions.calcItemCardSize(
          newItem,
          useMindmapSettings,
          newFontSize,
          newWidthMap
        );
      }

      // If this is not our first calculation for this item, then let's avoid unnecessary updates
      // by stripping out any values that are identical to their last ones
      // Strip out any changes that are identical to stored values.
      // This is because a lot of the time, some individual item might have changed that won't affect the
      // outer bounding box (e.g. date changes, but title is wider, so no effect)
      if (prevItem) {
        changes = omitBy(changes, (value, key) => {
          return areEquivalent(value, currSizes.get(key));
        });
      }

      s.update(id, d => d.merge(changes));
    });
  });
};

export default reducer;
